import {
    IFormSection, IForm, PickListItem, JsonItem, SectionStatus
} from '../../../../../shared/components/web-form/web-form.objects';

import { ContactInfoSection, ProjectDetailSection, AdminDetailsSection, RequestHistoryEngagement, BiospecimenRequestSection } from './request-form.section';
import { RequestFormExtended } from '../../scripts/request-form.objects';
import { RequestFormNavigation } from './request-form.navigation';
import { RequestFormView } from '../../scripts/request-form.view';
import { UserQuery } from '../../scripts/request-form.objects';
import _ from 'lodash';

export class RecoverRequestForm implements IForm {
    formSections: IFormSection[];
    pickLists: JsonItem[];
    currentItem: RequestFormExtended;
    defaultItem: RequestFormExtended;

    navigation: RequestFormNavigation;

    sideBarElement: JQuery<HTMLElement>;
    formElement: JQuery<HTMLElement>;
    confirmSubmitModal: JQuery<HTMLElement>;

    saveRoute: string;
    submitRoute: string;
    redirectRoute: string;

    saveBtn: JQuery<HTMLElement>;
    saveBtnLoading: JQuery<HTMLElement>;
    saveBtnComplete: JQuery<HTMLElement>;
    submitBtn: JQuery<HTMLElement>;
    submitBtnContainer: JQuery<HTMLElement>;
    submitBtnLoading: JQuery<HTMLElement>;

    isSaving: boolean = false;

    reviewView: RequestFormView;

    constructor(currentApplication: RequestFormExtended, pickLists: JsonItem[]) {
        this.currentItem = currentApplication;
        this.defaultItem = new RequestFormExtended();
        this.pickLists = pickLists;

        this.sideBarElement = $(".sidebar-container");
        this.formElement = $(".request-form");
        this.confirmSubmitModal = $("#confirm-submit-modal");

        this.saveRoute = "/secure/biospecimens/sample-request-form/request-form/request-form?handler=SaveApplication";
        this.submitRoute = "/secure/biospecimens/sample-request-form/request-form/request-form?handler=SubmitApplication";
        this.redirectRoute = "/secure/biospecimens/request-submit";

        this.formSections = [
            new ContactInfoSection(),
            new ProjectDetailSection(),
            new AdminDetailsSection(),
            new BiospecimenRequestSection(),
            new RequestHistoryEngagement()
        ];

        this.reviewView = new RequestFormView("form-review-container");

        this.initialize();

        this.sideBarElement.removeClass("d-none");
        this.formElement.removeClass("d-none");
    }

    initialize() {
        this.formSections.forEach(section => section.initialize(this));
        this.prepopulateForm();
        this.initializeCharCountHandlers();
        this.refreshAllCharCounts();
        this.navigation = new RequestFormNavigation(this);
        this.navigation.initialize();
        this.updateAllNavSections();
        this.initializeSaveSubmitBtns();
    }

    private prepopulateForm() {
        let applicationObj = _.cloneDeep(this.currentItem);

        this.formSections.forEach(section => {
            section.formFields.forEach(field => {
                if (field.forObjProperty) {
                    field.setValue(applicationObj);
                }
            })
        })
    }

    private initializeSaveSubmitBtns() {
        // Save Btn (Partial Save)
        this.saveBtn = this.sideBarElement.find(".save-progress-btn");
        this.saveBtnLoading = this.sideBarElement.find(".save-form-loading");
        this.saveBtnComplete = this.sideBarElement.find(".save-form-complete");

        this.saveBtn.on("click", () => {
            this.triggerSave();
        });

        this.submitBtn = this.confirmSubmitModal.find("#submit-button");
        this.submitBtnContainer = this.confirmSubmitModal.find(".submit-btn-container");
        this.submitBtnLoading = this.confirmSubmitModal.find(".submit-btn-loading");

        this.submitBtn.on("click", () => {
            this.triggerSubmit();
        })
    }

    async triggerSave() {
        if (this.isSaving) {
            return;
        }

        this.saveBtn.addClass('d-none');
        this.saveBtnLoading.removeClass('d-none');

        await this.saveForm();

        this.saveBtnLoading.addClass("d-none");
        this.saveBtnComplete.removeClass('d-none');

        setTimeout(() => {
            this.saveBtnComplete.addClass("d-none");
            this.saveBtn.removeClass("d-none");
        }, 1500)
    }

    async triggerSubmit() {
        if (this.isSaving) {
            return;
        }
        this.submitBtnContainer.addClass('d-none');
        this.submitBtnLoading.removeClass("d-none");

        let result = await this.submitForm();

        let origin = window.location.origin
        let targetUrl = `${origin}/secure/biospecimens/request-submit`;

        window.location.href = targetUrl; // Redirect to submit page
    }

    private updateAllNavSections() {
        this.formSections.forEach(section => {
            let status = section.getSectionStatus();
            this.navigation.updateSectionStatus(section.sectionId, status);
            section.formFields.forEach(field => {
                if (!field.shouldTriggerUpdate) {
                    field.shouldTriggerUpdate = true;
                }
            });
        })
    }

    triggerNavUpdate(sectionId: string, status: SectionStatus) {
        this.navigation.updateSectionStatus(sectionId, status);
    }

    async saveForm(): Promise<any> {
        if (this.isSaving) {
            return;
        }

        this.isSaving = true;
        let applicationObj = _.cloneDeep(this.currentItem);

        // Update Form
        this.formSections.forEach(section => {
            section.formFields.forEach(field => {
                if (field.forObjProperty) {
                    _.set(applicationObj, field.propertyName, field.getValue());
                }
            })
        })

        applicationObj.BiospecimenRequests?.forEach(req => {
            req.Quantity = (req.Quantity ?? "").toString();
            req.Volume = (req.Volume ?? "").toString();
        })

        let result: any;
        try {
            result = await $.ajax(this.saveRoute, {
                method: "POST",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                data: { data: JSON.stringify(applicationObj) }
            });
            this.isSaving = false;
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    async submitForm(): Promise<any> {
        if (this.isSaving) {
            return;
        }

        this.isSaving = true;
        let applicationObj = _.cloneDeep(this.currentItem);

        // Update Form
        this.formSections.forEach(section => {
            section.formFields.forEach(field => {
                if (field.forObjProperty) {
                    _.set(applicationObj, field.propertyName, field.getValue());
                }
            })
        })

        let result: any;
        try {
            result = await $.ajax(this.submitRoute, {
                method: "POST",
                headers: { "RequestVerificationToken": <string>$('input[name="__RequestVerificationToken"]').val() },
                data: { data: JSON.stringify(applicationObj) }
            });
            this.isSaving = false;
            return result;
        }
        catch (error) {
            alert("An error has occurred, please refresh the page and try again.");
            console.log(error);
        }
    }

    updateForm() {

    }

    updateReviewContent() {

        let applicationObj = _.cloneDeep(this.currentItem);

        // Update Form
        this.formSections.forEach(section => {
            section.formFields.forEach(field => {
                if (field.forObjProperty) {
                    _.set(applicationObj, field.propertyName, field.getValue());
                }
            })
        })

        let institutionTypePickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-institution-type").Json;
        let supportPickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-biostatistician-support").Json;
        let yesNoPickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-yes-no").Json;
        let fundingStatusPickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-funding-status")?.Json;
        let requestTypePickList: PickListItem[] = this.pickLists.find(list => list.Id == "pl-new-resubmitted-request")?.Json;
        let queryList: UserQuery[] = this.pickLists.find(list => list.Id == "user-queries").Json;

        this.reviewView.initialize(applicationObj, institutionTypePickList, supportPickList, yesNoPickList, fundingStatusPickList, requestTypePickList, queryList);
    }

    private initializeCharCountHandlers() {
        document.addEventListener("input", (event) => {
            const target = event.target as HTMLElement;
            if (!target) return;

            if (!target.classList.contains("word-count")) return;

            const field = target as HTMLTextAreaElement;

            const count = field.value.length;

            const formGroup = field.closest(".form-group");
            if (!formGroup) return;

            const counter = formGroup.querySelector(".word-count-num") as HTMLElement;
            if (counter) counter.textContent = count.toString();
        });
    }

    private refreshAllCharCounts() {
        const fields = document.querySelectorAll<HTMLTextAreaElement>(".word-count");
        fields.forEach(field => {
            const count = (field.value || "").length;

            const formGroup = field.closest(".form-group");
            if (!formGroup) return;

            const counter = formGroup.querySelector(".word-count-num") as HTMLElement;
            if (counter) counter.textContent = count.toString();
        });
    }

}